import axios from "axios";
import cogoToast from "cogo-toast";
// import { persistor } from '../store';

const { createSlice, createAsyncThunk, isAnyOf } = require("@reduxjs/toolkit");

// Access auth token
export const fetchAccessToken = createAsyncThunk(
  "auth/fetchAccessToken",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/auth/token", payload);
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// login API
export const login = createAsyncThunk(
  "auth/login",
  async (
    { userLoginId, password, tenantId },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await axios.post("/services/login", {
        userLoginId,
        password,
        tenantId,
        isB2B: true,
      });

      if (response.status === 200) {
        if (response?.data?.data?.message) {
          cogoToast.error(response.data.data.message, {
            position: "top-right",
            hideAfter: 5,
          });
        } else {
          cogoToast.success("Logged in successfully!", {
            position: "top-right",
            hideAfter: 5,
          });
        }
      }
      return response?.data?.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// logout API
export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const userDetails = auth?.userDetails || {};
      const response = await axios.post("/services/logout", {
        userLoginId: userDetails?.userLoginId,
      });

      if (response.status === 200) {
        cogoToast.success(response.data.successMessage, {
          position: "top-right",
          hideAfter: 5,
        });
      }
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// get all clients
export const getClientList = createAsyncThunk(
  "auth/getClientList",
  async ({ setClients }, { rejectWithValue }) => {
    try {
      const response = await axios.post("/services/getClientList", {
        webSiteId: "B2BWebStore",
      });

      if (response.status === 200) {
        setClients(response?.data?.data?.clientList || []);
      }
      return response?.data?.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// Get order Details
export const getOrderDetails = createAsyncThunk(
  "product/getOrderDetails",
  async ({ orderId, setOrderDetails }, thunkAPI) => {
    try {
      const encodedURI = `/services/getOrderDetail?inParams=${encodeURIComponent(
        JSON.stringify({ orderId, isUpdate: "Y" })
      )}`;
      const response = await axios.get(encodedURI);
      if (response.status === 200) {
        setOrderDetails(response?.data?.data?.orderDetails);
      }
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Get Agreement Terms API
export const getAgreementTerms = createAsyncThunk(
  "party/getAgreementTerms",
  async (
    {
      setAgreementTerms,
      agreementId,
      orderId = "",
    },
    thunkAPI
  ) => {
    try {
      const inParams = {
        agreementId,
        orderId,
      };
      const encodedURI = `/services/getAgreementTerms?inParams=${encodeURIComponent(
        JSON.stringify(inParams)
      )}`;
      const response = await axios.get(encodedURI);

      if (response.status === 200) {
        setAgreementTerms(response?.data?.data?.agreementTerms || []);
        return response.data;
      }
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response?.data);
    }
  }
);

//Get Agreement Items API
export const getAgreementItems = createAsyncThunk(
  "party/getAgreementItems",
  async (
    {
      setAgreementItems,
      agreementId,
      orderId = "",
    },
    thunkAPI
  ) => {
    try {
      const inParams = {
        agreementId,
        orderId,
      };
      const encodedURI = `/services/getAgreementItems?inParams=${encodeURIComponent(
        JSON.stringify(inParams)
      )}`;
      const response = await axios.get(encodedURI);

      if (response.status === 200) {
        setAgreementItems(response?.data?.data?.agreementItems || []);
        return response.data;
      }
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response?.data);
    }
  }
);

// Get Product Agreement Categories
export const getAgreementProductCategories = createAsyncThunk(
  "party/getAgreementProductCategories",
  async (
    {
      setAgreementProductCategory,
      agreementId,
      agreementItemSeqId,
      orderId = "",
    },
    thunkAPI
  ) => {
    try {
      const inParams = {
        agreementId,
        agreementItemSeqId,
        orderId,
      };
      const encodedURI = `/services/getAgreementProductCategories?inParams=${encodeURIComponent(
        JSON.stringify(inParams)
      )}`;
      const response = await axios.get(encodedURI);
      if (response.status === 200) {
        setAgreementProductCategory(
          response?.data?.data?.agreementProductCategories ?? []
        );
        return response.data;
      }
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response?.data);
    }
  }
);

// Get AgentInfo
export const getAgentInfo = createAsyncThunk(
  "product/getAgentInfo",
  async ({ setAgentInfo }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const agentPartyId = auth?.client?.partyId || "";
      const encodedURI = `/services/getAgentInfo?inParams=${encodeURIComponent(
        JSON.stringify({ agentPartyId })
      )}`;
      const response = await axios.get(encodedURI);
      if (response.status === 200) {
        setAgentInfo(response?.data?.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Get Agent Orders
export const getAgentOrders = createAsyncThunk(
  "product/getAgentOrders",
  async (
    {
      viewSize = 10,
      viewIndex = 0,
      sortField = "",
      appliedFilters,
      setAgentOrders,
      setOrganizations,
      setTotalRecords,
    },
    { getState, rejectWithValue }
  ) => {
    try {
      const { auth } = getState();
      const agentPartyId = auth?.client?.partyId || "";
      const params = {
        viewSize,
        viewIndex,
        sortField,
        ...appliedFilters,
      };
      const encodedURI = `/services/getAgentOrders?inParams=${encodeURIComponent(
        JSON.stringify(params)
      )}`;
      const response = await axios.get(encodedURI);
      if (response.status === 200) {
        setAgentOrders(response?.data?.data?.agentOrders);
        setOrganizations(response?.data?.data?.organizationNames);
        setTotalRecords(response?.data?.data?.totalRecords || 0);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Update order status
export const changeOrderStatus = createAsyncThunk(
  "product/changeOrderStatus",
  async ({ data }, thunkAPI) => {
    try {
      const response = await axios.post("/services/changeOrderStatus", data);
      if (response.status === 200) {
        cogoToast.success("Order status changed successfully!", {
          position: "top-right",
          hideAfter: 5,
        });
      }

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const getPerformSearch = createAsyncThunk(
  "party/getPerformSearch",
  async (
    {
      setPerformSearch,
      entityName,
      inputFields = {},
      orderBy = "description",
      noConditionFind = "Y",
    },
    thunkAPI
  ) => {
    try {
      const params = {
        entityName,
        noConditionFind,
        inputFields,
        orderBy,
      };
      const encodedURI = `/services/peformSearch?inParams=${encodeURIComponent(
        JSON.stringify(params)
      )}`;

      const response = await axios.get(encodedURI);
      if (response.status === 200) {
        const records = response?.data?.data?.records || [];
        setPerformSearch(records);
        return response.data;
      } else {
        return thunkAPI.rejectWithValue(
          response.data || "Unexpected Error occurred"
        );
      }
    } catch (error) {
      // Error Handling
      return thunkAPI.rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Get return info
export const getReturnInfo = createAsyncThunk(
  "product/getReturnInfo",
  async ({ orderId, setReturnInfo }, { rejectWithValue }) => {
    try {
      const encodedURI = `/services/getReturnInfo?inParams=${encodeURIComponent(
        JSON.stringify({ orderId })
      )}`;
      const response = await axios.get(encodedURI);
      if (response.status === 200) {
        setReturnInfo(response?.data?.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Create return
export const createReturn = createAsyncThunk(
  "product/createReturn",
  async ({ data, callback }, thunkAPI) => {
    try {
      const response = await axios.post("/services/createReturn", data);
      if (response.status === 200) {
        cogoToast.success("Return created successfully!", {
          position: "top-right",
          hideAfter: 5,
        });
        if (callback) callback();
      }

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Get return details
export const getReturnDetails = createAsyncThunk(
  "product/getReturnDetails",
  async ({ returnId, setReturnDetails }, { rejectWithValue }) => {
    try {
      const encodedURI = `/services/getReturnDetail?inParams=${encodeURIComponent(
        JSON.stringify({ returnId })
      )}`;
      const response = await axios.get(encodedURI);
      if (response.status === 200) {
        setReturnDetails(response?.data?.data?.returnDetails || {});
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Initialize Substitute OrderCart
export const initializeSubstituteOrder = createAsyncThunk(
  "product/initializeSubstituteOrder",
  async ({ data, callback }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/services/initializeSubstituteOrderCart",
        data
      );
      if (response.status === 200) {
        if (callback) callback();
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Update return header
export const updateReturnHeader = createAsyncThunk(
  "product/updateReturnHeader",
  async ({ data, callback }, thunkAPI) => {
    try {
      const response = await axios.post("/services/updateReturnHeader", data);
      if (response.status === 200) {
        cogoToast.success("Return status updated successfully!", {
          position: "top-right",
          hideAfter: 5,
        });
        if (callback) callback();
      }

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.response?.data || error.message);
    }
  }
);

const initialState = {
  accessToken: null,
  isLoading: false,
  client: null,
  shoppingCart: null,
  selectedCategories: [],
  tenantId: null,
  userDetails: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setClient: (state, action) => {
      state.client = action.payload;
    },
    setShoppingCart: (state, action) => {
      state.shoppingCart = action.payload;
    },
    setSelectedCategories: (state, action) => {
      state.selectedCategories = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        isAnyOf(
          fetchAccessToken.pending,
          login.pending,
          getClientList.pending,
          getOrderDetails.pending,
          changeOrderStatus.pending,
          getAgentOrders.pending,
          getAgentInfo.pending,
          createReturn.pending,
          getReturnInfo.pending,
          getReturnDetails.pending,
          updateReturnHeader.pending
        ),
        (state) => {
          state.isLoading = true;
        }
      )
      .addMatcher(isAnyOf(login.fulfilled), (state, action) => {
        state.isLoading = false;
        state.accessToken = action.payload.access_token;
        state.userDetails = action?.payload?.userDetails;
        if (action?.payload?.tenantId) {
          state.tenantId = action.payload.tenantId;
        }
      })
      .addMatcher(
        isAnyOf(
          login.rejected,
          getClientList.fulfilled,
          getClientList.rejected,
          getOrderDetails.fulfilled,
          getOrderDetails.rejected,
          changeOrderStatus.fulfilled,
          changeOrderStatus.rejected,
          getAgentOrders.fulfilled,
          getAgentOrders.rejected,
          getAgentInfo.fulfilled,
          getAgentInfo.rejected,
          createReturn.fulfilled,
          createReturn.rejected,
          getReturnInfo.fulfilled,
          getReturnInfo.rejected,
          getReturnDetails.fulfilled,
          getReturnDetails.rejected,
          updateReturnHeader.fulfilled,
          updateReturnHeader.rejected
        ),
        (state, action) => {
          state.isLoading = false;
        }
      )
      .addMatcher(isAnyOf(logout.fulfilled), (state, action) => {
        state.accessToken = "";
        state.accessToken = "";
        state.userDetails = null;
        state.tenantId = null;
        state.client = null;
        state.shoppingCart = null;
        state.selectedCategories = [];
        localStorage.clear("persist:B2B");
      });
  },
});

export const authSelector = (state) => state.auth;

export const { setClient, setShoppingCart, setSelectedCategories } =
  authSlice.actions;
export default authSlice.reducer;
